import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import { AuthLayout } from "@/components/auth/AuthLayout";
import {
  DefinirPasswordForm,
  passwordSchema,
  type DefinirPasswordValues,
} from "@/components/auth/DefinirPasswordForm";
import { Button } from "@/components/ui/button";
import { useResetPassword } from "@/hooks/useAuth";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";

/**
 * Define una nueva contraseña desde el enlace de "Recuperar contraseña"
 * (`RecuperarContrasena.tsx`). Lee `token` + `email` de la query string y
 * pega contra POST /api/reset-password.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const resetPassword = useResetPassword();
  const form = useForm<DefinirPasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "", password_confirmation: "" },
  });

  function handleSubmit(values: DefinirPasswordValues) {
    if (!token || !email) return;

    resetPassword.mutate(
      { token, email, ...values },
      {
        onSuccess: () => {
          toast.success("Tu contraseña fue actualizada correctamente.");
          navigate("/login");
        },
        onError: (error) => {
          const errores = parseValidationErrors(error);
          const camposConError = errores
            ? (["password", "password_confirmation"] as const).filter((campo) => errores[campo])
            : [];

          if (camposConError.length > 0) {
            for (const campo of camposConError) {
              form.setError(campo, { type: "server", message: errores![campo] });
            }
            return;
          }

          toast.error(
            getApiErrorMessage(error, "No pudimos actualizar tu contraseña. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  if (!token || !email) {
    return (
      <AuthLayout>
        <div className="mb-8">
          <div className="bg-destructive/10 text-destructive mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">Enlace inválido</h1>
          <p className="text-muted-foreground text-caption mt-2">
            Este enlace de recuperación no es válido. Solicita uno nuevo.
          </p>
        </div>

        <Button variant="outline" size="lg" className="w-full" asChild>
          <Link to="/recuperar-contrasena">Solicitar nuevo enlace</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className="bg-accent text-accent-foreground mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Nueva contraseña</h1>
        <p className="text-muted-foreground text-caption mt-2">
          Elige una contraseña segura para tu cuenta.
        </p>
      </div>

      <DefinirPasswordForm
        form={form}
        onSubmit={handleSubmit}
        isSubmitting={resetPassword.isPending}
        submitLabel="Guardar contraseña"
      />

      <Link
        to="/login"
        className="text-muted-foreground hover:text-foreground mt-8 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
      >
        Volver a iniciar sesión
        <ArrowRight className="h-4 w-4" />
      </Link>
    </AuthLayout>
  );
}
